import axiosInstance from '../api/axios';
import PricingPlans from './PricingPlans';
// LandingPage.js — öffentliche Infoseite unter `/mehr-infos`
// Struktur: Hero → App-Preview-Karussell (5 Screenshots) → Feature-Grid →
// Kampagnen-Bilder (die 4 Werbesprüche) → Preise (falls aktiv) → Final CTA.
import { useEffect, useState, useRef, useCallback } from 'react';
import { useVisibilityFlags } from '../hooks/useVisibilityFlags';
import { ArrowRight, Sun, Moon, ChevronLeft, ChevronRight, CalendarDays, FileText, FolderOpen, Users, Landmark, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import './LandingPage.css';



// Current UI screenshots, grouped in the order of a working day.
const APP_PREVIEWS = [
  {
    "id": "dashboard",
    "images": [
      "/marketing/io-current/dashboard.webp"
    ]
  },
  {
    "id": "calendar",
    "images": [
      "/marketing/io-current/calendar.webp"
    ]
  },
  {
    "id": "contacts",
    "images": [
      "/marketing/io-current/customers.webp",
      "/marketing/io-current/email.webp"
    ]
  },
  {
    "id": "assignment",
    "images": [
      "/marketing/io-current/assignment-editor.webp"
    ]
  },
  {
    "id": "workflow",
    "images": [
      "/marketing/io-current/assignments.webp"
    ]
  },
  {
    "id": "draft",
    "images": [
      "/marketing/io-current/invoice-editor.webp"
    ]
  },
  {
    "id": "invoices",
    "images": [
      "/marketing/io-current/invoices.webp"
    ]
  },
  {
    "id": "documents",
    "images": [
      "/marketing/io-current/documents.webp",
      "/marketing/io-current/templates.webp"
    ]
  },
  {
    "id": "accounting",
    "images": [
      "/marketing/io-current/accounting.webp",
      "/marketing/io-current/statistics.webp"
    ]
  },
  {
    "id": "sync",
    "images": [
      "/marketing/io-current/sync.webp"
    ]
  },
  {
    "id": "migration",
    "images": [
      "/marketing/io-current/migration.webp"
    ]
  }
];

// Kampagnen-Bilder (die 4 bestehenden Werbe-Motive) — alt-Texte via i18n.


const goToLogin = (mode = 'login') => {
  const url = mode === 'register' ? '/login?mode=register' : '/login';
  window.location.assign(url);
};

// Sprach-Toggle — inline im Header (nicht mehr fixed), sodass er nicht mit
// dem Anmelden-Button kollidiert.
const InlineLanguageToggle = ({ isLight }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { i18n, t } = useTranslation();
  const ref = useRef(null);
  const languages = [
    { code: 'de', label: 'Deutsch' },
    { code: 'en', label: 'English' },
    { code: 'fr', label: 'Français' },
  ];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    const escape = event => { if (event.key === 'Escape') setIsOpen(false); };
    document.addEventListener('pointerdown', handleClickOutside);
    document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('pointerdown', handleClickOutside); document.removeEventListener('keydown', escape); };
  }, []);

  const handleChange = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem('language', code);
    setIsOpen(false);
  };
  const currentLabel =
    languages.find((l) => i18n.language?.startsWith(l.code))?.label || 'Deutsch';

  const buttonStyle = isLight
    ? { backgroundColor: '#ffffff', color: '#1d4ed8', border: '1px solid #e5e7eb' }
    : {};

  return (
    <div ref={ref} className="relative" data-testid="landing-language-orb">
      <button
        data-ui-variant="landing"
        onClick={() => setIsOpen((p) => !p)}
        aria-expanded={isOpen}
        aria-label={t('landing.nav.language')}
        style={buttonStyle}
        className={`flex items-center px-3 py-1.5 rounded-md text-xs transition-all ${
          isLight
            ? 'hover:bg-gray-50 shadow-sm'
            : 'bg-gray-800/60 backdrop-blur-sm border border-gray-700/50 text-gray-300 hover:text-yellow-400 hover:border-gray-600'
        }`}
        data-testid="landing-language-toggle"
      >
        <span>{currentLabel}</span>
      </button>
      {isOpen && (
        <div
          className={`absolute right-0 mt-1 rounded-md overflow-hidden whitespace-nowrap min-w-[110px] z-50 ${
            isLight
              ? 'bg-white shadow-lg border border-gray-200'
              : 'bg-gray-800/95 backdrop-blur-sm border border-gray-700/50 shadow-lg'
          }`}
        >
          {languages.map((lang) => {
            const isActive = i18n.language?.startsWith(lang.code);
            return (
              <button
        data-ui-variant="landing"
                key={lang.code}
                onClick={() => handleChange(lang.code)}
                className={`block w-full text-center px-3 py-1.5 text-xs transition-colors ${
                  isLight
                    ? isActive
                      ? 'text-blue-600 bg-gray-100'
                      : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
                    : isActive
                    ? 'text-yellow-400 bg-gray-700/50'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700/30'
                }`}
                data-testid={`landing-language-${lang.code}`}
              >
                {lang.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ThemeOrb bleibt fixed unten rechts (kein Konflikt mit Header).
const ThemeOrb = () => {
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const isLight = theme === 'light';
  const style = isLight
    ? { backgroundColor: '#ffffff', color: '#2563eb', border: '1px solid #e5e7eb' }
    : {};
  return (
    <button
        data-ui-variant="landing"
      onClick={toggleTheme}
      aria-label={isLight ? t('landing.themeOrb.activateDark') : t('landing.themeOrb.activateLight')}
      title={isLight ? t('landing.themeOrb.titleDark') : t('landing.themeOrb.titleLight')}
      style={style}
      className={`fixed bottom-6 right-6 z-50 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
        isLight
          ? 'hover:bg-gray-50 shadow-sm'
          : 'bg-gray-800/60 backdrop-blur-sm border border-gray-700/50 text-yellow-400 hover:border-gray-600'
      }`}
      data-testid="landing-theme-toggle"
    >
      {isLight ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
    </button>
  );
};

// Manuell bedienbare App-Vorschau ohne automatische Bildwechsel.
const PreviewCarousel = ({ isLight }) => {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const total = APP_PREVIEWS.length;

  const go = useCallback((next) => {
    setIndex((i) => (next + total) % total);
  }, [total]);

  const current = APP_PREVIEWS[index];

  return (
    <div className="w-full" data-testid="landing-preview-carousel">
      {/* Bild-Container fix (kein Zoom, keine Buttons IM Bild — sie stehen
          außerhalb, damit sie zwischen Slides absolut stationär bleiben) */}
      <div
        className={`landing-preview-visual relative overflow-hidden ${
          isLight
            ? 'bg-gray-100 border border-b-0 border-gray-200'
            : 'bg-[#0a1a3d] border border-b-0 border-white/10'
        }`}
      >
        <div className={`landing-preview-stage ${current.images.length > 1 ? 'has-split' : ''}`}>
          {APP_PREVIEWS.map((slide, slideIndex) => (
            <div key={slide.id} hidden={slideIndex !== index} className={`landing-preview-frame ${slide.images.length > 1 ? 'is-split' : ''}`}>
              {slide.images.map((src, imageIndex) => (
                <a key={src} href={src} target="_blank" rel="noopener noreferrer" aria-label={t(`landing.previews.items.${slide.id}.title`)}>
                  <img src={src} width="1280" height="900" alt={t(`landing.previews.items.${slide.id}.title`)}
                    loading="eager" fetchPriority={slideIndex === 0 ? 'high' : 'low'} decoding="async"
                    data-testid={slideIndex === index ? `landing-preview-image-${slide.id}-${imageIndex}` : undefined} />
                </a>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Untertitel-Leiste UNTER dem Bild — solide, mit klarem Kontrast
          in Light + Dark Mode, feste Höhe damit alle Slides gleich groß */}
      <div
        className={`shadow-2xl px-6 sm:px-8 py-5 border min-h-[110px] flex flex-col justify-center ${
          isLight
            ? 'bg-white text-gray-900 border-gray-200'
            : 'bg-gray-900 text-white border-white/10'
        }`}
        data-testid="landing-preview-caption" aria-live="polite" aria-atomic="true"
      >
        <div className="landing-preview-caption-stack">
          {APP_PREVIEWS.map((slide, slideIndex) => (
            <div key={slide.id} className="landing-preview-caption-item" aria-hidden={slideIndex !== index}>
              <h3 className={`text-lg font-semibold mb-1 ${isLight ? 'text-gray-900' : 'text-white'}`}>
                {t(`landing.previews.items.${slide.id}.title`)}
              </h3>
              <p className={`text-sm leading-relaxed ${isLight ? 'text-gray-600' : 'text-gray-300'}`}>
                {t(`landing.previews.items.${slide.id}.caption`)}
              </p>
            </div>
          ))}
        </div>
      </div>

      <p className="landing-preview-note">{t('landing.previews.exampleNote')}</p>
      {/* Steuer-Leiste UNTERHALB des Bildes: Prev — Dots — Next.
          Stationär, unabhängig davon welches Bild gerade läuft. */}
      <div className="mt-5 flex items-center justify-center gap-3">
        <button
        data-ui-variant="landing"
          type="button"
          onClick={() => go(index - 1)}
          aria-label={t('landing.previews.prev')}
          className={`w-10 h-10 rounded-full flex items-center justify-center transition ${
            isLight
              ? 'bg-white hover:bg-gray-100 text-gray-800 shadow-md border border-gray-200'
              : 'bg-gray-800 hover:bg-gray-700 text-white border border-white/10'
          }`}
          data-testid="landing-preview-prev"
        >
          <ChevronLeft className="w-5 h-5 relative -left-px" />
        </button>

        <span className="landing-preview-count" aria-hidden="true">{index + 1} / {total}</span>
        <div className="landing-preview-dots flex items-center gap-2">
          {APP_PREVIEWS.map((p, i) => (
            <button
        data-ui-variant="landing"
              key={p.id}
              type="button"
              onClick={() => setIndex(i)}
              aria-current={i === index ? 'true' : undefined}
              aria-label={t('landing.previews.show', { title: t(`landing.previews.items.${p.id}.title`) })}
              className="landing-preview-dot"
              data-testid={`landing-preview-dot-${i}`}
            ><span aria-hidden="true" /></button>
          ))}
        </div>

        <button
        data-ui-variant="landing"
          type="button"
          onClick={() => go(index + 1)}
          aria-label={t('landing.previews.next')}
          className={`w-10 h-10 rounded-full flex items-center justify-center transition ${
            isLight
              ? 'bg-white hover:bg-gray-100 text-gray-800 shadow-md border border-gray-200'
              : 'bg-gray-800 hover:bg-gray-700 text-white border border-white/10'
          }`}
          data-testid="landing-preview-next"
        >
          <ChevronRight className="w-5 h-5 relative left-px" />
        </button>
      </div>
    </div>
  );
};

const LandingPage = ({ onGoBackToAuth } = {}) => {
  const { pricing_visible: pricingVisible } = useVisibilityFlags({ publicPricing: true });
  const [publicPlans, setPublicPlans] = useState({});
  useEffect(() => {
    let active = true;
    setPublicPlans({});
    if (pricingVisible) axiosInstance.get('/billing/public-plans').then(({data}) => {
      if (active) setPublicPlans(data.plans || {});
    }).catch(() => {});
    return () => { active = false; };
  }, [pricingVisible]);
  const { theme } = useTheme();
  const { t } = useTranslation();
  const isLight = theme === 'light';

  // Callback-Bindings: bei in-app Slide (PublicShell) rufen wir Callbacks auf,
  // sonst fällt es auf Full-Page-Load zurück.
  const handleLoginClick = (mode = 'login') => {
    if (onGoBackToAuth) {
      onGoBackToAuth(mode);
    } else {
      goToLogin(mode);
    }
  };



  const rootBg = isLight
    ? 'bg-white text-gray-900'
    : 'bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white';
  const headerBg = isLight
    ? 'bg-white/90 backdrop-blur-md border-b border-gray-200'
    : 'bg-gray-900/80 backdrop-blur-md border-b border-white/10';
  const bodyText = isLight ? 'text-gray-700' : 'text-gray-300';
  const mutedText = isLight ? 'text-gray-500' : 'text-gray-400';
  const headingText = isLight ? 'text-gray-900' : 'text-white';
  const cardBg = isLight
    ? 'bg-gray-50 border-gray-200 hover:bg-gray-100 hover:border-yellow-500/50'
    : 'bg-white/[0.03] border-white/10 hover:bg-white/[0.06] hover:border-yellow-400/30';
  const cardText = isLight ? 'text-gray-800' : 'text-gray-200';
  const dividerBorder = isLight ? 'border-gray-200' : 'border-white/10';

  return (
    <div className={`io-landing min-h-screen antialiased ${rootBg}`} data-testid="landing-page">
      <ThemeOrb />

      {/* Header — Anmelden-Button links neben Sprach-Toggle, kein Overlap */}
      <header className={`landing-header fixed top-0 inset-x-0 z-40 ${headerBg}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <a
            href="/"
            onClick={(e) => {
              if (onGoBackToAuth) {
                e.preventDefault();
                onGoBackToAuth();
              }
            }}
            className="flex items-center gap-2 min-w-0 cursor-pointer group"
            data-testid="landing-logo"
            aria-label={t('landing.nav.backToLogin')}
          >
            {/* Platzhalter für SharedBrand — unsichtbar, hält aber Layout */}
            <span
              className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 invisible"
              style={{ backgroundColor: '#facc15' }}
              aria-hidden="true"
            >
              <span
                style={{
                  fontSize: '14px',
                  fontWeight: 800,
                  color: '#000000',
                  fontFamily:
                    'system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif',
                  lineHeight: 1,
                  letterSpacing: '0.5px',
                }}
              >
                IO
              </span>
            </span>
            <span
              className={`hidden sm:inline font-semibold truncate ${
                isLight ? 'text-black' : 'text-yellow-400'
              }`}
            >
              Interpreter&apos;s Office
            </span>
          </a>

          {/* Cluster rechts: Anmelden LINKS von Sprache */}
          <div className="flex items-center gap-2 sm:gap-3">
            <button
        data-ui-variant="landing"
              onClick={() => handleLoginClick('login')}
              className="bg-yellow-400 hover:bg-yellow-300 text-gray-900 font-semibold px-4 sm:px-5 py-1.5 sm:py-2 rounded-full text-sm transition shadow-lg shadow-yellow-400/20 whitespace-nowrap"
              data-testid="landing-nav-login"
            >
              {t('landing.nav.login')}
            </button>
            <InlineLanguageToggle isLight={isLight} />
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="landing-hero pt-32 pb-10 sm:pb-14 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <div
            className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium mb-8 ${
              isLight
                ? 'bg-yellow-100 border border-yellow-400 text-gray-900'
                : 'bg-yellow-400/10 border border-yellow-400/30 text-yellow-400'
            }`}
          >
            <span className="w-2 h-2 rounded-full bg-yellow-400"></span>
            {t('landing.hero.betaBadge')}
          </div>
          <h1
            className={`text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-tight mb-6 ${headingText}`}
          >
            {t('landing.hero.titlePrefix')}{' '}
            <span className={isLight ? 'text-blue-700' : 'text-yellow-400'}>
              {t('landing.hero.titleAccent')}
            </span>
          </h1>
          <p
            className={`max-w-2xl mx-auto text-lg leading-relaxed mb-2 ${bodyText}`}
          >
            {t('landing.hero.sub1')}
          </p>
          <p
            className={`max-w-2xl mx-auto text-lg leading-relaxed mb-10 ${bodyText}`}
          >
            {t('landing.hero.sub2')}
          </p>

        </div>
      </section>

      <nav className="landing-jump-nav" aria-label={t('landing.nav.sections')}>
        <a href="#io-features">{t('landing.nav.features')}</a>
        <a href="#io-preview">{t('landing.nav.preview')}</a>
        <a href="#io-about">{t('landing.nav.about')}</a>
        {pricingVisible && <a href="#io-pricing">{t('landing.pricing.kicker')}</a>}
      </nav>

      {/* App-Preview-Karussell */}
      <section className="pb-20 px-4 sm:px-6" id="io-preview" data-testid="landing-previews">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <div
              className={`text-sm font-semibold mb-3 tracking-wider uppercase ${
                isLight ? 'text-blue-700' : 'text-yellow-400'
              }`}
            >
              {t('landing.previews.kicker')}
            </div>
            <h2
              className={`text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight ${headingText}`}
            >
              {t('landing.previews.title')}
            </h2>
          </div>
          <PreviewCarousel isLight={isLight} />
        </div>
      </section>

      {/* Features */}
      <section id="io-features" className={`py-20 px-6 border-t ${dividerBorder}`}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <div
              className={`text-sm font-semibold mb-3 tracking-wider uppercase ${
                isLight ? 'text-blue-700' : 'text-yellow-400'
              }`}
            >
              {t('landing.features.kicker')}
            </div>
            <h2
              className={`text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight ${headingText}`}
            >
              {t('landing.features.title')}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="landing-features">
            {[CalendarDays, FileText, Users, FolderOpen, Landmark, Mail].map((Icon, index) => {
              const feature = t(`landing.features.cards.${index}`, { returnObjects:true });
              return <article key={index} className={`landing-feature flex items-start gap-3 p-4 rounded-xl border ${cardBg}`}>
                <span className="landing-feature-icon"><Icon size={22} strokeWidth={1.7} aria-hidden="true" /></span>
                <div><h3 className={cardText}>{feature.title}</h3><p className={mutedText}>{feature.description}</p></div>
              </article>;
            })}
          </div>
        </div>
      </section>

      {/* Kampagnen-Bilder mit den 4 Werbesprüchen — jetzt WEITER UNTEN */}
      <section
        className={`py-20 px-6 border-t ${dividerBorder}`}
        id="io-about" data-testid="landing-campaign"
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div
              className={`text-sm font-semibold mb-3 tracking-wider uppercase ${
                isLight ? 'text-blue-700' : 'text-yellow-400'
              }`}
            >
              {t('landing.campaign.kicker')}
            </div>
            <h2
              className={`text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight ${headingText}`}
            >
              {t('landing.campaign.title')}
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {['plan', 'document', 'invoice', 'overview'].map(key => (
              <article key={key} className={`p-6 rounded-lg border ${cardBg}`}>
                <h3 className={`text-lg font-semibold mb-2 ${headingText}`}>{t(`landing.campaign.benefits.${key}.title`)}</h3>
                <p className={`text-sm leading-relaxed ${bodyText}`}>{t(`landing.campaign.benefits.${key}.text`)}</p>
              </article>
            ))}
          </div>
          <p className={`text-center text-sm mt-8 italic ${mutedText}`}>
            {t('landing.campaign.footnote')}
          </p>
        </div>
      </section>

      {/* Preise — nur wenn Admin-Toggle aktiv */}
      {pricingVisible && (
        <section id="io-pricing"
          className={`py-24 px-6 border-t ${dividerBorder}`}
          data-testid="landing-pricing"
        >
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-14">
              <div
                className={`text-sm font-semibold mb-3 tracking-wider uppercase ${
                  isLight ? 'text-blue-700' : 'text-yellow-400'
                }`}
              >
                {t('landing.pricing.kicker')}
              </div>
              <h2
                className={`text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight ${headingText}`}
              >
                {t('landing.pricing.title')}
              </h2>
            </div>
            <PricingPlans plans={publicPlans} publicPage handleCheckout={() => handleLoginClick('register')} />

          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className={`landing-closing py-20 px-6 border-t ${dividerBorder}`}>
        <div className="max-w-3xl mx-auto text-center">
          <h2
            className={`text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-5 ${headingText}`}
          >
            {t('landing.finalCta.title')}
          </h2>
          <button
        data-ui-variant="landing"
            onClick={() => handleLoginClick('register')}
            className="inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-gray-900 font-semibold px-8 py-4 rounded-full text-lg transition shadow-lg shadow-yellow-400/20"
            data-testid="landing-final-cta"
            style={{ marginTop: '7px' }}
          >
            {t('landing.finalCta.cta')}
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className={`py-10 px-6 border-t ${dividerBorder}`}>
        <div
          className={`max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-sm ${mutedText}`}
        >
          <div className="flex items-center gap-3">
            <span
              className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
              style={{
                backgroundColor: '#facc15',
                boxShadow: '0 2px 12px rgba(250, 204, 21, 0.3)',
              }}
            >
              <span
                style={{
                  fontSize: '10px',
                  fontWeight: 800,
                  color: '#000000',
                  fontFamily:
                    'system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif',
                  lineHeight: 1,
                  letterSpacing: '0.5px',
                }}
              >
                IO
              </span>
            </span>
            <span>{t('landing.footer.company', { year: new Date().getFullYear() })}</span>
          </div>
          <div className="flex items-center gap-6">
            <a
              href="/impressum"
              className={
                isLight ? 'hover:text-gray-900 transition' : 'hover:text-white transition'
              }
            >
              {t('landing.footer.imprint')}
            </a>
            <a
              href="/datenschutz"
              className={
                isLight ? 'hover:text-gray-900 transition' : 'hover:text-white transition'
              }
            >
              {t('landing.footer.privacy')}
            </a>
            <a
              href="/agb"
              className={
                isLight ? 'hover:text-gray-900 transition' : 'hover:text-white transition'
              }
            >
              {t('landing.footer.terms')}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
